import React, { useMemo, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import './SignUpPage.scss';
import { validateEmail } from '../../helpers/Validators';
import { loadStripe } from '@stripe/stripe-js';
import axiosInstance from '../../axios/axiosAuthInterceptor';
import { trackEvent } from '../../analytics';

type PlanUrl = 'basis' | 'student' | 'small-org';

type FieldKey =
    | 'firstName'
    | 'lastName'
    | 'email'
    | 'phone'
    | 'studentNumber'
    | 'university-email'
    | 'university'
    | 'orgName'
    | 'orgEmail'
    | 'orgPhone'
    | 'message'
    | 'birthDate';

type FieldConfig = {
    key: FieldKey;
    type: 'text' | 'email' | 'tel' | 'textarea';
    required?: boolean;
};

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY || '');

const mapPlanToEnum = (p: PlanUrl): 'Student' | 'Basic' | 'SmallOrg' => {
    if (p === 'student') return 'Student';
    if (p === 'small-org') return 'SmallOrg';
    return 'Basic';
};

const UNIVERSITIES = [
    { value: 'uva', label: 'Universiteit van Amsterdam', domain: 'uva.nl' },
    { value: 'vu', label: 'Vrije Universiteit Amsterdam', domain: 'vu.nl' },
    { value: 'leiden', label: 'Universiteit Leiden', domain: 'universiteitleiden.nl' },
    { value: 'eur', label: 'Erasmus Universiteit Rotterdam', domain: 'eur.nl' },
    { value: 'uu', label: 'Universiteit Utrecht', domain: 'uu.nl' },
    { value: 'rug', label: 'Rijksuniversiteit Groningen', domain: 'rug.nl' },
    { value: 'ru', label: 'Radboud Universiteit', domain: 'ru.nl' },
    { value: 'maastricht', label: 'Maastricht University', domain: 'maastrichtuniversity.nl' },
    { value: 'tilburg', label: 'Tilburg University', domain: 'tilburguniversity.edu' },
    { value: 'tud', label: 'Technische Universiteit Delft', domain: 'tudelft.nl' },
    { value: 'tue', label: 'Technische Universiteit Eindhoven', domain: 'tue.nl' },
    { value: 'utwente', label: 'Universiteit Twente', domain: 'utwente.nl' },
    { value: 'wur', label: 'Wageningen University & Research', domain: 'wur.nl' },
    { value: 'ou', label: 'Open Universiteit', domain: 'ou.nl' },
    { value: 'demo', label: 'Demo', domain: '' },
] as const;

const getDomainForUniversity = (uniValue: string | undefined) => UNIVERSITIES.find((u) => u.value === uniValue)?.domain;

const emailMatchesDomain = (email: string, allowedDomain?: string) => {
    if (!email || !allowedDomain) return true;
    const at = email.lastIndexOf('@');
    if (at < 0) return false;
    const domain = email.slice(at + 1).toLowerCase();
    const allow = allowedDomain.toLowerCase();
    return domain === allow || domain.endsWith('.' + allow);
};

const SignupPage: React.FC = () => {
    const { t, i18n } = useTranslation();
    const history = useHistory();
    const { search } = useLocation();
    const planUrl: PlanUrl = (new URLSearchParams(search).get('plan') as PlanUrl) || 'basis';
    const planEnum = mapPlanToEnum(planUrl);

    const [usersTotal] = useState<number>(1); // optional default for small-org
    const [submitting, setSubmitting] = useState(false);
    const [values, setValues] = useState<Record<FieldKey, string>>({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        studentNumber: '',
        university: '',
        'university-email': '',
        orgName: '',
        orgEmail: '',
        orgPhone: '',
        message: '',
        birthDate: '',
    });
    const [agree, setAgree] = useState(false);

    const isSmall = planUrl === 'small-org';

    const titleKey: Record<PlanUrl, string> = {
        basis: 'pages.signup.title.basis',
        student: 'pages.signup.title.student',
        'small-org': 'pages.signup.title.smallOrg',
    };

    // ---- Fields per plan
    const fields: FieldConfig[] = useMemo(() => {
        if (planUrl === 'student') {
            return [
                { key: 'firstName', type: 'text', required: true },
                { key: 'lastName', type: 'text', required: true },
                { key: 'university-email', type: 'email', required: true },
                { key: 'phone', type: 'tel', required: true },
                { key: 'studentNumber', type: 'text', required: true },
                { key: 'university', type: 'text', required: true },
                { key: 'birthDate', type: 'text', required: true },
            ];
        }
        if (planUrl === 'small-org') {
            return [
                { key: 'firstName', type: 'text', required: true },
                { key: 'lastName', type: 'text', required: true },
                { key: 'orgName', type: 'text', required: true },
                { key: 'orgEmail', type: 'email', required: true },
                { key: 'orgPhone', type: 'tel', required: true },
                { key: 'message', type: 'textarea' },
            ];
        }
        // basis
        return [
            { key: 'firstName', type: 'text', required: true },
            { key: 'lastName', type: 'text', required: true },
            { key: 'email', type: 'email', required: true },
            { key: 'phone', type: 'tel', required: true },
        ];
    }, [planUrl]);

    const update = (k: FieldKey, v: string) => setValues((s) => ({ ...s, [k]: v }));

    // ---- Validation (no errors before typing)
    const isEmailOK = (k: FieldKey) => {
        if (!k.toLowerCase().includes('university-email')) return true;
        const v = values[k] || '';
        if (v.length === 0) return true; // empty is OK until required check kicks in
        if (!validateEmail(v)) return false;

        // Extra rule for student plan: domain must match selected university
        if (planUrl === 'student' && k === 'university-email') {
            const uniDomain = getDomainForUniversity(values.university);
            return emailMatchesDomain(v, uniDomain);
        }
        return true;
    };
    const isPhoneOK = (k: FieldKey) => {
        if (!k.toLowerCase().includes('phone')) return true;
        const digits = (values[k] || '').replace(/\D/g, '');
        return digits.length === 0 || digits.length >= 9;
    };

    const isBirthDateOK = () => {
        const v = values.birthDate.trim();
        if (v.length === 0) return true;

        if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return false;

        const [y, m, d] = v.split('-').map(Number);
        const birth = new Date(y, m - 1, d);

        if (birth.getFullYear() !== y || birth.getMonth() !== m - 1 || birth.getDate() !== d) {
            return false;
        }

        const today = new Date();

        let age = today.getFullYear() - y;
        const hadBirthdayThisYear = today.getMonth() > m - 1 || (today.getMonth() === m - 1 && today.getDate() >= d);
        if (!hadBirthdayThisYear) age -= 1;

        return age >= 12 && age <= 25;
    };

    const canSubmit =
        fields.every((f) => (f.required ? values[f.key].trim().length > 0 : true)) &&
        fields.every((f) => isEmailOK(f.key) && isPhoneOK(f.key)) &&
        (planUrl !== 'student' || isBirthDateOK()) &&
        agree;

    // Extract the server-side error string from an axios error response.
    // Returns e.g. "UserExists" or "StripeError: No such price" instead of the generic axios message.
    const extractServerError = (err: any): string => {
        const serverMsg: string | undefined = err?.response?.data?.error;
        if (serverMsg) return serverMsg;
        const status: number | undefined = err?.response?.status;
        if (status) return `HTTP ${status}: ${err?.message ?? 'Unknown error'}`;
        return err?.message ?? 'Unknown error';
    };

    // 1) Register user first (generic endpoint for all plans)
    const registerUser = async (): Promise<{ userId: string }> => {
        const contactEmail =
            planUrl === 'small-org'
                ? values.orgEmail
                : planUrl === 'student'
                ? values['university-email']
                : values.email;
        const contactPhone = planUrl === 'small-org' ? values.orgPhone : values.phone;

        const payload: any = {
            licenseType: planEnum,
            firstName: values.firstName,
            lastName: values.lastName,
            email: contactEmail,
            phone: contactPhone,
            orgName: values.orgName || undefined,
            message: values.message || undefined,
            studentNumber: values.studentNumber || undefined,
            university: values.university || undefined,
            birthDate: values.birthDate || undefined,
            usersTotal: isSmall ? usersTotal : undefined,
            language: i18n.language === 'nl' ? 0 : 1,
            rememberMe: true,
        };

        try {
            const res = await axiosInstance.post('/user/register/license', payload);
            return res.data; // { userId }
        } catch (err: any) {
            throw new Error(extractServerError(err));
        }
    };

    // 2) Paid plans → Stripe Checkout
    const startStripeCheckout = async (userId: string) => {
        const contactEmail =
            planUrl === 'small-org'
                ? values.orgEmail
                : planUrl === 'student'
                ? values['university-email']
                : values.email;

        let res: any;
        try {
            res = await axiosInstance.post('/stripe/create-checkout-session', {
                licenseType: planEnum,
                email: contactEmail,
                firstName: values.firstName,
                lastName: values.lastName,
                orgName: values.orgName,
                usersTotal: isSmall ? usersTotal : undefined,
                userId,
                returnUrl: window.location.origin,
            });
        } catch (err: any) {
            throw new Error(extractServerError(err));
        }

        trackEvent('Signup', 'Account Created', planEnum);

        const { id, url } = res.data;
        const stripe = await stripePromise;

        if (stripe && id) {
            const { error } = await stripe.redirectToCheckout({ sessionId: id });
            if (!error) return;
            if (url) {
                window.location.href = url;
                return;
            }
            throw new Error(`Stripe redirect failed: ${error.message}`);
        }

        if (url) {
            window.location.href = url;
            return;
        }

        throw new Error('Stripe redirect unavailable: no session ID or URL returned.');
    };

    // 3) Student → success page (no Stripe)
    const finishStudent = () => {
        trackEvent('Signup', 'Account Created', 'Student');
        trackEvent('Signup', 'Student University', values.university || undefined);
        history.push('/signup/success?student=1');
    };

    // CTA
    const handleSubmitAll = async () => {
        if (!canSubmit || submitting) return;
        setSubmitting(true);
        try {
            const { userId } = await registerUser();
            if (planEnum === 'Student') {
                finishStudent();
            } else {
                await startStripeCheckout(userId);
            }
        } catch (err: any) {
            const msg: string = err?.message ?? '';
            if (msg === 'UserExists') {
                alert(t('pages.signup.errors.emailInUse'));
            } else {
                // Show the raw server/Stripe error so problems are immediately visible.
                alert(
                    msg ||
                        (planEnum === 'Student'
                            ? t('pages.signup.errors.studentRegister')
                            : t('pages.signup.errors.checkout')),
                );
            }
        } finally {
            setSubmitting(false);
        }
    };

    const onSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        handleSubmitAll();
    };

    return (
        <div className="mainLoginPage">
            <div id="signuppage" className="search">
                <form className="card signup-card" onSubmit={onSubmit}>
                    <div className="title">{t(titleKey[planUrl])}</div>

                    {fields.map((f) => {
                        const label = t(`pages.signup.fields.${f.key}.label`);
                        const placeholder = t(`pages.signup.fields.${f.key}.placeholder`);
                        const hasError = !isEmailOK(f.key) || !isPhoneOK(f.key);

                        if (planUrl === 'student' && f.key === 'university') {
                            return (
                                <label key={f.key} className={`field ${hasError ? 'has-error' : ''}`}>
                                    <span className="label">{label}</span>
                                    <select
                                        className="university-select body body-normal"
                                        value={values.university}
                                        onChange={(e) => update('university', e.target.value)}
                                    >
                                        <option value="" disabled>
                                            {t('pages.signup.fields.university.placeholder')}
                                        </option>
                                        {UNIVERSITIES.map((u) => (
                                            <option key={u.value} value={u.value}>
                                                {u.label}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                            );
                        }

                        // Default render for other fields
                        return (
                            <label key={f.key} className={`field ${hasError ? 'has-error' : ''}`}>
                                <span className="label">{label}</span>
                                {f.type === 'textarea' ? (
                                    <textarea
                                        rows={3}
                                        placeholder={placeholder}
                                        value={values[f.key]}
                                        onChange={(e) => update(f.key, e.target.value)}
                                    />
                                ) : f.key === 'birthDate' ? (
                                    <input
                                        type="date"
                                        placeholder={placeholder}
                                        value={values.birthDate}
                                        onChange={(e) => update('birthDate', e.target.value)}
                                        required={f.required}
                                        min={(() => {
                                            const d = new Date();
                                            d.setFullYear(d.getFullYear() - 25);
                                            return d.toISOString().slice(0, 10);
                                        })()}
                                        max={(() => {
                                            const d = new Date();
                                            d.setFullYear(d.getFullYear() - 12);
                                            return d.toISOString().slice(0, 10);
                                        })()}
                                    />
                                ) : (
                                    <input
                                        type={f.type}
                                        placeholder={placeholder}
                                        value={values[f.key]}
                                        onChange={(e) => update(f.key, e.target.value)}
                                        required={f.required}
                                    />
                                )}

                                {planUrl === 'student' &&
                                    f.key === 'university-email' &&
                                    values.university &&
                                    values['university-email'] &&
                                    !isEmailOK('university-email') && (
                                        <small className="hint error">
                                            {t('pages.signup.validation.studentEmailDomain', {
                                                domain: getDomainForUniversity(values.university),
                                            })}
                                        </small>
                                    )}
                                {planUrl === 'student' &&
                                    f.key === 'birthDate' &&
                                    values.birthDate &&
                                    !isBirthDateOK() && (
                                        <small className="hint error">
                                            {t('pages.signup.validation.ageRange', { min: 12, max: 25 })}
                                        </small>
                                    )}
                            </label>
                        );
                    })}

                    <label className="terms">
                        <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
                        <span>
                            <Trans
                                i18nKey="pages.signup.terms"
                                components={{
                                    1: (
                                        <a
                                            href="/documents/algemene-voorwaarden.pdf"
                                            target="_blank"
                                            rel="noreferrer"
                                        />
                                    ),
                                }}
                            />
                        </span>
                    </label>

                    <button className="button button-ready" type="submit" disabled={!canSubmit || submitting}>
                        {t('pages.signup.cta.create')}
                    </button>

                    <p className="footer-note">
                        {t('pages.signup.haveAccount')} <Link to="/login">{t('pages.signup.login')}</Link>
                    </p>
                </form>
            </div>
        </div>
    );
};

export default SignupPage;
